/**
 * Upload a file via PUT to a presigned URL with progress reporting.
 * Uses XMLHttpRequest so we can listen to upload.onprogress (fetch does not expose it).
 */

export interface UploadProgressCallback {
  (percent: number): void;
}

export interface UploadFileWithProgressOptions {
  headers?: Record<string, string>;
}

/**
 * Upload file to presigned S3 URL with progress callback.
 * @param uploadUrl - Presigned PUT URL
 * @param file - File to upload
 * @param onProgress - Called with 0-100 as upload progresses
 * @param options - Optional extra headers (e.g. S3 SSE from authoring API)
 * @returns Promise that resolves when upload completes, rejects on error
 */
export function uploadFileWithProgress(
  uploadUrl: string,
  file: File,
  onProgress: UploadProgressCallback,
  options: UploadFileWithProgressOptions = {},
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.responseType = 'text';

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && event.total > 0) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(Math.min(percent, 100));
      }
    });

    xhr.addEventListener('load', () => {
      const response = new Response(xhr.responseText, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: new Headers(
          xhr
            .getAllResponseHeaders()
            .split('\r\n')
            .filter(Boolean)
            .map((line) => {
              const [key, ...parts] = line.split(': ');
              return [key, parts.join(': ')];
            })
            .filter(([k]) => k) as [string, string][]
        )
      });
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(response);
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('PUT', uploadUrl);
    const contentType =
      options.headers?.['Content-Type'] ?? (file.type || 'application/octet-stream');
    xhr.setRequestHeader('Content-Type', contentType);
    Object.entries(options.headers ?? {}).forEach(([key, value]) => {
      if (key !== 'Content-Type') {
        xhr.setRequestHeader(key, value);
      }
    });
    xhr.send(file);
  });
}
